import debug from 'debug';
import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { widgetIdSelector } from '@/state/config';
import { IframeEventType } from '@/ts';
import { sessionIdSelector } from '../state/session';
export var AnalyticsEvent;
(function (AnalyticsEvent) {
    AnalyticsEvent["LauncherClicked"] = "launcher_clicked";
    AnalyticsEvent["ChatExpanded"] = "chat_expanded";
    AnalyticsEvent["MinimizeIconClicked"] = "minimize_icon_clicked";
    AnalyticsEvent["CloseIconClicked"] = "close_icon_clicked";
    AnalyticsEvent["CloseOpeningMessageClick"] = "close_opening_message_click";
    AnalyticsEvent["SessionStart"] = "session_start";
    AnalyticsEvent["SessionEnd"] = "session_end";
    AnalyticsEvent["MessageClick"] = "message_click";
    AnalyticsEvent["TermsAndConditionsClicked"] = "terms_and_conditions_clicked";
    AnalyticsEvent["TermsAndConditionsLinkClicked"] = "terms_and_conditions_link_clicked";
    AnalyticsEvent["WelcomeFormSubmitted"] = "welcome_form_submitted";
    AnalyticsEvent["RatingClicked"] = "rating_clicked";
    AnalyticsEvent["RatingSubmitted"] = "rating_submitted";
    AnalyticsEvent["PopoverReviewClicked"] = "popover_review_clicked";
    AnalyticsEvent["PopoverInactivityEndChatClicked"] = "popover_inactivity_end_chat_clicked";
    AnalyticsEvent["PopoverInactivityContinueClicked"] = "popover_inactivity_continue_clicked";
    AnalyticsEvent["PopoverEndChatClicked"] = "popover_end_chat_clicked";
    AnalyticsEvent["PopoverEndChatContinueClicked"] = "popover_end_chat_continue_clicked";
    AnalyticsEvent["PopoverExpiredNewClicked"] = "popover_expired_new_clicked";
    AnalyticsEvent["PopoverExpiredEndClicked"] = "popover_expired_end_clicked";
    AnalyticsEvent["PopoverResolvedContinueClicked"] = "resolved_popover_continue_clicked";
    AnalyticsEvent["PopoverResolvedEndChatClicked"] = "resolved_popover_end_chat_clicked";
    AnalyticsEvent["PopoverReviewSkipAndCloseClicked"] = "popover_review_skip_and_close_clicked";
    AnalyticsEvent["PopoverFeedbackCloseClicked"] = "popover_feedback_close_clicked";
})(AnalyticsEvent || (AnalyticsEvent = {}));
/**
 * Hook that provides analytics tracking functionality.
 * Formats events according to the required structure and can be used
 * to track various webchat interactions.
 */
export const useAnalytics = () => {
    const sessionId = useRecoilValue(sessionIdSelector);
    const widgetId = useRecoilValue(widgetIdSelector);
    const log = useMemo(() => debug(`moveo:iframe:${widgetId}:controller`), [widgetId]);
    /**
     * Publish an analytics event to the parent widget.
     */
    const publishAnalyticsEvent = useCallback((eventData) => {
        if (window.parent && widgetId) {
            log('Publishing analytics event', eventData);
            window.parent.postMessage({
                type: `${widgetId}${IframeEventType.AnalyticsEvent}`,
                data: eventData,
            }, '*');
        }
    }, [log, widgetId]);
    /**
     * Format an analytics event with the required structure
     */
    const formatEvent = useCallback((eventName, additionalProperties = {}) => {
        return {
            timestamp: new Date().toISOString(),
            event: eventName,
            session_id: sessionId,
            properties: Object.assign({}, additionalProperties),
        };
    }, [sessionId]);
    /**
     * Track an analytics event
     */
    const trackEvent = useCallback((eventName, additionalProperties = {}) => {
        const formattedEvent = formatEvent(eventName, additionalProperties);
        publishAnalyticsEvent(formattedEvent);
    }, [formatEvent, publishAnalyticsEvent]);
    return {
        trackEvent,
        formatEvent,
    };
};
